import { API_BASE_URL, fetchWithTimeout, handleResponse } from "./shared";
import type {
  MarketDataCredentialStatus,
  ValidateCredentialResponse,
} from "./market-data";

// ============================================================================
// Broker Credentials API (SFWL-006 Phase 2)
// ============================================================================

export type BrokerId = "cqg";

/** Masked read of a stored org broker credential. Secrets are `****abcd`. */
export interface BrokerCredentialDto {
  id: string;
  broker: BrokerId;
  status: MarketDataCredentialStatus;
  lastValidatedAt: string | null;
  maskedPayload: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface BrokerCredentialsListResponse {
  credentials: BrokerCredentialDto[];
}

/**
 * Tenant scoping: the `x-tenant-id` header is attached globally by
 * `fetchWithTimeout` from the `activeTenantId` registered by
 * TenantThemeProvider — the controller 400s without an active tenant, so these
 * calls only succeed on a resolved tenant subdomain.
 */
export const brokerCredentialsApi = {
  /** List the active org's BYO broker credentials (masked). Org-admin scoped. */
  async list(): Promise<BrokerCredentialsListResponse> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/broker-credentials`);
    return handleResponse<BrokerCredentialsListResponse>(response);
  },

  /**
   * Create or replace the org credential for a broker. `payload` is the opaque
   * vendor shape (CQG `{ username, password, clientAppId }`); encrypted at rest.
   */
  async upsert(
    broker: BrokerId,
    payload: Record<string, unknown>
  ): Promise<BrokerCredentialDto> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/broker-credentials`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ broker, payload }),
    });
    return handleResponse<BrokerCredentialDto>(response);
  },

  /** Run the credential's "test connection" check and return its new status. */
  async validate(id: string): Promise<ValidateCredentialResponse> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/broker-credentials/${id}/validate`,
      { method: "POST" }
    );
    return handleResponse<ValidateCredentialResponse>(response);
  },

  /** Delete an org broker credential. */
  async remove(id: string): Promise<void> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/broker-credentials/${id}`,
      { method: "DELETE" }
    );
    if (!response.ok) {
      await handleResponse<void>(response);
    }
  },
};
